import React from 'react'
import { Container, Paper, Typography, Grid, TextField, Checkbox, FormControlLabel, Button, Stack, Chip } from '@mui/material'
import { $getRoot } from 'lexical'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../hooks/useToast'
import { useToastMessages } from '../utils/toastMessages'

import { ListNode, ListItemNode } from '@lexical/list'
import { LinkNode } from '@lexical/link'

const theme = {}
const initialConfig = { 
  namespace: 'wg-editor', 
  theme, 
  onError: (e: Error) => console.error(e), 
  editable: true,
  nodes: [ListNode, ListItemNode, LinkNode]
}
const API = import.meta.env.VITE_API_BASE_URL || '/api'

type FormState = {
  title: string
  city: string
  district: string
  price: string
  size: string
  furnished: boolean
  availableFrom: string
  duration: string
  imageUrl: string
  description: string
  contactEmail: string
}

const createInitialForm = (email?: string): FormState => ({
  title: '',
  city: '',
  district: '',
  price: '',
  size: '',
  furnished: false,
  availableFrom: '',
  duration: '',
  imageUrl: '',
  description: '',
  contactEmail: email ?? ''
})

const NewListing: React.FC = () => {
  const { user } = useAuth()
  const { toastPromise } = useToast()
  const toastMessages = useToastMessages()
  const [form, setForm] = React.useState<FormState>(() => createInitialForm(user?.email))
  const [submitting, setSubmitting] = React.useState(false)
  const [editorKey, setEditorKey] = React.useState(0)

  React.useEffect(() => {
    if (user?.email) {
      setForm(prev => (prev.contactEmail ? prev : { ...prev, contactEmail: user.email as string }))
    }
  }, [user])

  const set = (k: keyof FormState, v: any) => setForm(prev => ({ ...prev, [k]: v }))

  const resetForm = React.useCallback(() => {
    setForm(createInitialForm(user?.email))
    setEditorKey(k => k + 1)
  }, [user])

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)

    const promise = fetch(`${API}/wg/listings`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: form.title,
        city: form.city,
        district: form.district || undefined,
        price: Number(form.price),
        size: Number(form.size),
        furnished: form.furnished,
        availableFrom: form.availableFrom || undefined,
        duration: form.duration || undefined,
        imageUrl: form.imageUrl || undefined,
        description: form.description,
        contactEmail: form.contactEmail || user?.email
      })
    }).then(async (response) => {
      if (!response.ok) {
        throw new Error('Create failed')
      }
      return response
    })

    toastPromise(promise, {
      loading: toastMessages.saving(),
      success: () => {
        resetForm()
        return toastMessages.listingCreated()
      },
      error: toastMessages.listingError(),
      onFinally: () => {
        setSubmitting(false)
      },
    })
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper sx={{ p: { xs: 2, md: 3 } }}>
        <Stack spacing={3}>
          <div>
            <Typography variant="h5" fontWeight={800} gutterBottom>Neue WG‑Anzeige</Typography>
            <Typography variant="body1" color="text.secondary">
              Teile deine WG mit der Community. Pflichtfelder sind mit * markiert.
            </Typography>
            {user?.email && (
              <Chip sx={{ mt: 1 }} color="primary" label={`Eingeloggt als ${user.email}`} />
            )}
          </div>

          <form onSubmit={onSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}><TextField label="Titel" fullWidth required value={form.title} onChange={e => set('title', e.target.value)} /></Grid>
              <Grid item xs={12} md={4}><TextField label="Stadt" fullWidth required value={form.city} onChange={e => set('city', e.target.value)} /></Grid>
              <Grid item xs={12} md={4}><TextField label="Stadtteil" fullWidth value={form.district} onChange={e => set('district', e.target.value)} /></Grid>
              <Grid item xs={6} md={4}><TextField label="Preis (€)" type="number" inputProps={{ min: 0 }} fullWidth required value={form.price} onChange={e => set('price', e.target.value)} /></Grid>
              <Grid item xs={6} md={4}><TextField label="Größe (m²)" type="number" inputProps={{ min: 1 }} fullWidth required value={form.size} onChange={e => set('size', e.target.value)} /></Grid>
              <Grid item xs={12} md={4}><FormControlLabel control={<Checkbox checked={form.furnished} onChange={e => set('furnished', e.target.checked)} />} label="Möbliert" /></Grid>
              <Grid item xs={12} md={4}><TextField label="Verfügbar ab" type="date" InputLabelProps={{ shrink: true }} fullWidth value={form.availableFrom} onChange={e => set('availableFrom', e.target.value)} /></Grid>
              <Grid item xs={12} md={4}><TextField label="Dauer" fullWidth value={form.duration} onChange={e => set('duration', e.target.value)} placeholder="z. B. 6-12 Monate" /></Grid>
              <Grid item xs={12} md={4}><TextField label="Kontakt E-Mail" type="email" required fullWidth value={form.contactEmail} onChange={e => set('contactEmail', e.target.value)} helperText="Wir zeigen diese Adresse Interessenten an." /></Grid>
              <Grid item xs={12}><TextField label="Bild URL" fullWidth value={form.imageUrl} onChange={e => set('imageUrl', e.target.value)} placeholder="https://..." /></Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom>Beschreibung</Typography>
                <LexicalComposer key={editorKey} initialConfig={initialConfig}>
                  <RichTextPlugin contentEditable={<ContentEditable style={{ minHeight: 160, border: '1px solid #ddd', borderRadius: 8, padding: 12 }} />} placeholder={null as any} ErrorBoundary={LexicalErrorBoundary} />
                  <HistoryPlugin />
                  <ListPlugin />
                  <LinkPlugin />
                  <OnChangePlugin onChange={(editorState) => {
                    editorState.read(() => { set('description', $getRoot().getTextContent()) })
                  }} />
                </LexicalComposer>
              </Grid>
              <Grid item xs={12}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <Button type="submit" variant="contained" disabled={submitting}>
                    {submitting ? 'Speichern...' : 'Speichern'}
                  </Button>
                  <Button type="button" variant="text" onClick={resetForm}>
                    Formular zurücksetzen
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </form>
        </Stack>
      </Paper>
    </Container>
  )
}

export default NewListing
